import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { TripWithRelations } from "@ckgo2/shared/types";
import { Plus, Search, Plane, Edit, Trash2, Calendar, MapPin, Copy } from "lucide-react";
import { formatPrice } from "@ckgo2/shared/utils";

export default function TripsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<TripWithRelations[]>>("/api/admin/trips"),
  });

  const duplicateMutation = useMutation({
    mutationFn: (tripId: string) =>
      api.post<ApiResponse<TripWithRelations>>(`/api/admin/trips/${tripId}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      if (response.data?.id) {
        navigate(`/trips/${response.data.id}`);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/trips/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const trips = data?.data || [];

  // Filter trips
  const filteredTrips = trips.filter((trip) => {
    const matchesSearch =
      trip.title?.toLowerCase().includes(search.toLowerCase()) ||
      trip.destination?.name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || trip.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (error) {
    return (
      <div className="card text-error-600 text-center">Error loading trips. Please try again.</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trips</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage trip catalog</p>
        </div>
        <Link to="/trips/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          New trip
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search trips..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : filteredTrips.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <Plane className="mb-2 h-12 w-12 text-gray-300" />
            <p>No trips found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Trip
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Destination
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Price
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTrips.map((trip) => (
                <tr key={trip.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {trip.featuredImage ? (
                        <img
                          src={trip.featuredImage}
                          alt={trip.title}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                          <Plane className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{trip.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">/{trip.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-300">
                        {trip.destination?.name || "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatPrice(trip.priceFrom)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-300">
                        {trip.durationDays} days
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {trip.status === "published" ? (
                      <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                        Published
                      </span>
                    ) : trip.status === "draft" ? (
                      <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400">
                        Draft
                      </span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Archived
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => duplicateMutation.mutate(trip.id)}
                        disabled={duplicateMutation.isPending}
                        className="btn-ghost btn-sm"
                        title="Duplicate trip"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <Link to={`/trips/${trip.id}`} className="btn-ghost btn-sm" title="Edit">
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(trip.id, trip.title)}
                        disabled={deleteMutation.isPending}
                        className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
